import { ref, computed, toValue, type Ref, type ComputedRef, type MaybeRefOrGetter } from 'vue'
import { PhoneVerdict } from '~/enums/PhoneVerdict'
import type { CountryCode } from '~/enums/CountryCode'
import { PhoneNumber } from '~/utils/PhoneNumber'
import { resolvePhoneMessage } from '~/composables/resolvePhoneMessage'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'

interface UsePhoneInputOptions {
  required?: MaybeRefOrGetter<boolean>
  initialValue?: string
  country?: MaybeRefOrGetter<CountryCode>
  onCountryDetected?: (_country: CountryCode) => void
}

interface UsePhoneInputReturn {
  phoneValue: Ref<string>
  cleanPhone: ComputedRef<string>
  phoneError: ComputedRef<string>
  handleBeforeInput: (_event: Event) => void
  handleInput: (_event: Event) => void
  handleBlur: () => void
  syncExternal: (_value: string) => void
  validate: () => boolean
  reset: () => void
  isValid: ComputedRef<boolean>
  isDirty: Ref<boolean>
  touched: Ref<boolean>
}

function inputOf(event: Event): HTMLInputElement {
  const target = event.target
  if (target instanceof HTMLInputElement) return target
  throw new TypeError('A phone input event must originate from an input element')
}

// The caret position in `display` after the same number of digits as `digitsBefore`.
function caretAfterDigits(display: string, digitsBefore: number): number {
  if (digitsBefore === 0) return 0
  let seen = 0
  for (let index = 0; index < display.length; index += 1) {
    if (/\d/u.test(display.charAt(index))) seen += 1
    if (seen === digitsBefore) return index + 1
  }
  return display.length
}

export function usePhoneInput(options?: UsePhoneInputOptions): UsePhoneInputReturn {
  const {
    required = false,
    initialValue = '',
    country = PhoneNumber.defaultCountry,
    onCountryDetected
  } = options ?? {}
  const t = usePresentationTranslator()

  const phoneValue = ref(PhoneNumber.display(initialValue))
  const isDirty = ref(false)
  // Set by a keystroke carrying characters the field never accepts (e.g. letters);
  // cleared by the next accepted keystroke.
  const hasInvalidChars = ref(false)
  // Feedback appears only after blur or an explicit validate(); the field stays neutral while focused.
  const touched = ref(false)

  const parsed = computed(() => PhoneNumber.parse(phoneValue.value, toValue(country)))
  const cleanPhone = computed(() => parsed.value.national)

  const phoneError = computed(() => {
    if (!isDirty.value) return ''
    if (!touched.value) return ''

    if (hasInvalidChars.value) {
      return resolvePhoneMessage(t, PhoneVerdict.INVALID_CHARACTERS)
    }

    const { verdict } = parsed.value
    if (verdict === PhoneVerdict.EMPTY && !toValue(required)) return ''
    return resolvePhoneMessage(t, verdict)
  })

  function adopt(raw: string): void {
    const selected = toValue(country)
    phoneValue.value = PhoneNumber.display(raw, selected)
    const detected = PhoneNumber.parse(raw, selected).country
    if (detected !== selected) onCountryDetected?.(detected)
  }

  // The selection an edit replaces, captured before the browser applies it; consumed by the next input.
  let selectionBeforeEdit: { start: number } | null = null

  function handleBeforeInput(event: Event): void {
    const target = inputOf(event)
    selectionBeforeEdit = target.selectionStart === null ? null : { start: target.selectionStart }
  }

  function handleInput(event: Event): void {
    const target = inputOf(event)
    const previous = phoneValue.value
    const rejected = PhoneNumber.hasInvalidCharacters(target.value)
    // The browser has already inserted the rejected character(s) into the
    // live DOM value ahead of this handler running, so `selectionStart` here
    // already reflects the cursor position AFTER that insertion.
    const cursorAfterInsertion = target.selectionStart
    const insertedLength = target.value.length - previous.length

    const editStart = selectionBeforeEdit
    selectionBeforeEdit = null

    isDirty.value = true
    hasInvalidChars.value = rejected

    // A rejected keystroke never changes `phoneValue`, so Vue may not re-sync the
    // DOM node the browser already mutated; force the previous text back.
    if (rejected) {
      target.value = previous

      // Setting `.value` programmatically moves the caret to the end; restore it.
      const derivedPos =
        cursorAfterInsertion === null ? null : Math.max(0, cursorAfterInsertion - insertedLength)
      const restoredPos = editStart === null ? derivedPos : editStart.start
      if (restoredPos !== null) target.setSelectionRange(restoredPos, restoredPos)
    } else {
      const caret = target.selectionStart
      const rawDigitsBeforeCaret =
        caret === null ? null : PhoneNumber.digitCount(target.value.slice(0, caret))
      // A calling code the edit absorbed leads the digits and is not part of the displayed number.
      const absorbedDigits = Math.max(
        0,
        PhoneNumber.digitCount(target.value) -
          PhoneNumber.parse(target.value, toValue(country)).national.length
      )
      const digitsBeforeCaret =
        rawDigitsBeforeCaret === null ? null : Math.max(0, rawDigitsBeforeCaret - absorbedDigits)
      adopt(target.value)
      // An edit that normalizes to the current model leaves Vue nothing to patch; restore the mask here.
      if (target.value !== phoneValue.value) {
        target.value = phoneValue.value
        if (digitsBeforeCaret !== null) {
          const position = caretAfterDigits(phoneValue.value, digitsBeforeCaret)
          target.setSelectionRange(position, position)
        }
      }
    }
  }

  function handleBlur(): void {
    touched.value = true
  }

  // A parent value carrying a rejected character keeps the last accepted text visible and the
  // field invalid; any other value replaces the field and clears the rejection.
  function syncExternal(value: string): void {
    const rejected = PhoneNumber.hasInvalidCharacters(value)
    hasInvalidChars.value = rejected
    if (rejected) return
    adopt(value)
  }

  // An empty entry is acceptable only when the field is not required.
  const isValid = computed(() => {
    const { verdict } = parsed.value
    const acceptable =
      verdict === PhoneVerdict.VALID || (verdict === PhoneVerdict.EMPTY && !toValue(required))
    return acceptable && !hasInvalidChars.value
  })

  function validate(): boolean {
    isDirty.value = true
    touched.value = true
    return isValid.value
  }

  function reset(): void {
    phoneValue.value = PhoneNumber.display(initialValue)
    isDirty.value = false
    hasInvalidChars.value = false
    touched.value = false
  }

  return {
    phoneValue,
    cleanPhone,
    phoneError,
    handleBeforeInput,
    handleInput,
    handleBlur,
    syncExternal,
    validate,
    reset,
    isValid,
    isDirty,
    touched
  }
}
